import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { Refresh } from "@material-ui/icons";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Toolbar,
} from "@material-ui/core";
import { useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { appUrl } from "../../../../../utils/config";
import {
  presentFormatedData,
  presentDate,
} from "../functionalComponents/dataFormatFunctions";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";
import PaginationInput from "../models/pagination/PaginationInput";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    backgroundColor: "#ffffff",
    minHeight: "100vh",
  },
  headerDivider: {
    borderBottom: "1px solid #ddd",
    marginBottom: theme.spacing(1),
    marginTop: 0,
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderBottom: "none",
    boxShadow: "none",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
    cursor: "pointer",
    "&:hover": {
      borderBottom: "2px solid black",
    },
  },
  backIcon: {
    paddingLeft: theme.spacing(1),
    fontSize: "small",
    color: "#000",
    cursor: "pointer",
  },
  backText: {
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "16px",
    color: "#000",
    marginLeft: theme.spacing(0.5),
  },
  headerTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#000",
    marginLeft: theme.spacing(2),
  },
  contentSection: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(6),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  tabsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  tabsLeft: {
    display: "flex",
    alignItems: "center",
  },
  syncGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  syncRefreshRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    cursor: "pointer",
    flexWrap: "wrap",
    gap: "8px",
  },
  lastSynced: {
    color: "gray",
    marginRight: "4px",
    whiteSpace: "nowrap",
    fontSize: "12px",
  },
  refreshText: {
    textDecoration: "underline",
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    marginRight: "4px",
  },
  perPageControl: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "4px",
    width: "100%",
  },
  perPageLabel: {
    fontSize: "14px",
    fontWeight: "bold",
    marginRight: "4px",
    whiteSpace: "nowrap",
  },
  reportBox: {
    border: "1px solid #ddd",
    borderRadius: "4px",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: "#fff",
  },
  heavyFont: {
    fontWeight: 500,
  },
  grdAlgnrow: {
    display: "flex",
    flexDirection: "row",
  },
  tableContainer: {
    marginTop: theme.spacing(1.5),
    border: "1px solid #ddd",
    borderRadius: "8px",
    overflowX: "auto",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  headerCell: {
    backgroundColor: "#f9f9fc",
    fontWeight: "bold",
    padding: "10px 12px",
    fontSize: "14px",
    color: "#000",
    whiteSpace: "normal",
    wordWrap: "break-word",
    border: "none",
    borderBottom: "2px solid #ddd",
    verticalAlign: "middle",
    lineHeight: "1.4",
  },
  dataCell: {
    fontSize: "15px",
    padding: "10px 16px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    color: "#333",
    border: "none",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "middle",
    lineHeight: "1.5",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  errorText: {
    color: "#d32f2f",
    padding: theme.spacing(2),
    textAlign: "center",
  },
  tabs: {
    minHeight: 36,
    "& .MuiTab-root": {
      minHeight: 36,
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.9rem",
      padding: "6px 16px",
    },
  },
}));

const TABLE_HEADERS = [
  { id: "lastUpdatedDate", label: "Date & Time", align: "left" },
  { id: "eventType", label: "Event Type", align: "left" },
  { id: "eventByUser", label: "Event By", align: "left" },
  { id: "updateByOrganization", label: "Organization", align: "left" },
  { id: "batchStatus", label: "Batch Status", align: "left" },
  { id: "batchUOM", label: "UOM", align: "left" },
  { id: "batchSize", label: "Batch Size", align: "right" },
  { id: "producedQuantity", label: "Produced Quantity", align: "right" },
  { id: "readyForDispatchQuantity", label: "Ready for Dispatch Qty", align: "right" },
  { id: "controlSampleQuantity", label: "Control Sample Qty", align: "right" },
];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 25, 50];

export default function InactiveBatchHistory({ batchDetailsProp, onClose }) {
  const classes = useStyles();
  const location = useLocation();

  const batchDetails = batchDetailsProp ?? location.state?.batchDetails ?? [];
  const batches = Array.isArray(batchDetails) ? batchDetails : [batchDetails];

  const [activeTab, setActiveTab] = useState(0);
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const activeBatch = batches[activeTab] || {};
  const vendorBatchNumber =
    activeBatch.vendorBatchNumber || activeBatch.batchNumber || "";

  const fetchBatchHistory = useCallback(
    (batchNumber) => {
      if (!batchNumber) return;
      setLoading(true);
      setError(null);
      axios
        .post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDeliveryScheduleBatchAuditReport`,
          { vendorBatchNumber: batchNumber }
        )
        .then((res) => {
          if (res.data.status) {
            setAuditData(res.data.data || []);
            setLastSynced(new Date());
            setCurrentPage(1);
          } else {
            setAuditData([]);
            setError(res.data.errorDescription || "No data found.");
          }
          setLoading(false);
        })
        .catch(() => {
          setAuditData([]);
          setError("An error occurred while fetching data. Please try again.");
          setLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    fetchBatchHistory(vendorBatchNumber);
  }, [vendorBatchNumber, fetchBatchHistory]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const handleBack = () => {
    if (onClose) onClose();
    else window.history.back();
  };

  const handleRefresh = () => {
    fetchBatchHistory(vendorBatchNumber);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(auditData.length / rowsPerPage));
  const paginatedData = auditData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const headerData = auditData[0] || {};

  return (
    <Box className={classes.root}>
      {/* Header: Back button + page title */}
      <Box className={classes.headerSection}>
        <div className={classes.backButton} onClick={handleBack}>
          <ArrowBackIosIcon className={classes.backIcon} />
          <Typography className={classes.backText}>Back</Typography>
        </div>
        <Typography className={classes.headerTitle}>
          Collaboration – Delivered/Deleted POs – View Batch history
        </Typography>
      </Box>
      <Box className={classes.headerDivider}></Box>

      <Box className={classes.contentSection}>
      {/* Tabs (left) | Last synced + Refresh + PER PAGE (right) */}
      <div className={classes.tabsRow}>
        <div className={classes.tabsLeft}>
          {batches.length > 0 && (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
            >
              {batches.map((batch, idx) => (
                <Tab
                  key={idx}
                  label={`Batch ${batch.vendorBatchNumber || batch.batchNumber || idx + 1}`}
                />
              ))}
            </Tabs>
          )}
        </div>

        <div className={classes.syncGroup}>
          <div className={classes.syncRefreshRow}>
            {lastSynced && (
              <Typography variant="body2" className={classes.lastSynced}>
                {`Last synced @ ${moment(lastSynced).format("HH:mm:ss; DD MMMM, YYYY")}`}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="primary"
              className={classes.refreshText}
              onClick={handleRefresh}
            >
              Refresh
              <Refresh color="primary" fontSize="small" style={{ marginLeft: 4 }} />
            </Typography>
          </div>
          <div className={classes.perPageControl}>
            <Typography variant="body2" className={classes.perPageLabel}>
              PER PAGE
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {/* Batch Audit Report box */}
      <div className={classes.reportBox}>
        <Grid item xs={12} style={{ padding: "1rem" }}>
          <Toolbar>
            <Grid container alignItems="baseline" spacing={2}>
              <Grid item xs={12} style={{ textAlign: "center" }}>
                <Typography variant="h6">Batch Audit Report</Typography>
              </Grid>

              {/* Row 1 */}
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>Batch No :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">{presentFormatedData(headerData.vendorBatchNumber)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>PO No :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">{presentFormatedData(headerData.scheduleAgreementNumber)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>PO Line No :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2">{presentFormatedData(headerData.scheduleLineItemNumber)}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>Material Code &amp; Description :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <Typography variant="body2">{presentFormatedData(headerData.fgCodeAndDescription)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>Vendor Name :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <Typography variant="body2">{presentFormatedData(headerData.vendorName)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>MFG Start Date :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <Typography variant="body2">{headerData.batchStartDate ? presentDate(headerData.batchStartDate) : "NA"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12} sm={6} md={4} className={classes.grdAlgnrow}>
                <Grid container spacing={1} direction="row" alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" className={classes.heavyFont}>MFG Finish Date :&nbsp;</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <Typography variant="body2">{headerData.batchEndDate ? presentDate(headerData.batchEndDate) : "NA"}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        </Grid>

        {/* History Table */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress color="primary" />
          </div>
        ) : error ? (
          <Typography className={classes.errorText}>{error}</Typography>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {TABLE_HEADERS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      className={classes.headerCell}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell align="left" className={classes.dataCell}>
                        {row.lastUpdatedDate
                          ? convertToLocaleDateTime(row.lastUpdatedDate).toUpperCase()
                          : "NA"}
                      </TableCell>
                      <TableCell align="left" className={classes.dataCell}>
                        {presentFormatedData(row.eventType)}
                      </TableCell>
                      <TableCell align="left" className={classes.dataCell}>
                        {presentFormatedData(row.eventByUser)}
                      </TableCell>
                      <TableCell align="left" className={classes.dataCell}>
                        {presentFormatedData(row.updateByOrganization)}
                      </TableCell>
                      <TableCell align="left" className={classes.dataCell}>
                        {presentFormatedData(row.batchStatus)}
                      </TableCell>
                      <TableCell align="left" className={classes.dataCell}>
                        {presentFormatedData(row.batchUOM)}
                      </TableCell>
                      <TableCell align="right" className={classes.dataCell}>
                        {presentFormatedData(row.batchSize)}
                      </TableCell>
                      <TableCell align="right" className={classes.dataCell}>
                        {presentFormatedData(row.producedQuantity)}
                      </TableCell>
                      <TableCell align="right" className={classes.dataCell}>
                        {presentFormatedData(row.readyForDispatchQuantity)}
                      </TableCell>
                      <TableCell align="right" className={classes.dataCell}>
                        {presentFormatedData(row.controlSampleQuantity)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_HEADERS.length}
                      align="center"
                      className={classes.dataCell}
                    >
                      No Records Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination inside the card */}
        {auditData.length > 0 && (
          <div className={classes.paginationContainer}>
            <PaginationInput
              totalPages={totalPages}
              page={currentPage}
              handlePageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
      </Box>
    </Box>
  );
}
